import React, { useState } from "react";
import { useQuery } from "@apollo/client";
import { Spin, Alert } from "antd";
import { useTranslation } from "react-i18next";
// Icons
import {
  MediumSquareFilled,
  GoogleSquareFilled,
  FacebookFilled,
  ContactsFilled,
  SoundFilled,
  YoutubeFilled,
  TikTokFilled,
  OpenAIFilled,
} from "@ant-design/icons";
// GraphQL
import GET_SURVEY_QUESTION from "graphql/queries/GetSurveyQuestion";
import { CheckboxChangeEvent } from "antd/lib/checkbox";
// Styles
import {
  Container,
  Question,
  UpperCaseHeading,
  AnswersCard,
  CardContent,
  IconContainer,
  StyledCheckbox,
  ContinueButton,
  ResponsiveGrid,
} from "./MultipleChoicesQuestionStyle";

type IconMapping = {
  [key: number]: JSX.Element;
};

const iconMapping: IconMapping = {
  27: <MediumSquareFilled style={{ fontSize: "24px" }} />,
  28: <GoogleSquareFilled style={{ fontSize: "24px" }} />,
  29: <FacebookFilled style={{ fontSize: "24px" }} />,
  30: <ContactsFilled style={{ fontSize: "24px" }} />,
  31: <SoundFilled style={{ fontSize: "24px" }} />,
  32: <YoutubeFilled style={{ fontSize: "24px" }} />,
  33: <TikTokFilled style={{ fontSize: "24px" }} />,
  34: <OpenAIFilled style={{ fontSize: "24px" }} />,
};
interface ReferralSourceProps {
  onChange: (data: { sources: number[] }) => void;
  onComplete: () => void;
  createSurveyResponse: (surveyQuestionId: number, answerId: number) => void;
}
const ReasonForLearning = ({
  onChange,
  onComplete,
  createSurveyResponse,
}: ReferralSourceProps) => {
  const [selectedSources, setSelectedSources] = useState<number[]>([]);
  const { t } = useTranslation("registration_survey");

  const { error, loading, data } = useQuery(GET_SURVEY_QUESTION, {
    variables: {
      order: 5,
      surveyType: 0,
    },
  });

  if (loading) return <Spin />;
  if (error) return <Alert message="Error loading question" type="error" />;

  const surveyQuestion = data?.surveyQuestion;

  if (!surveyQuestion)
    return <Alert message="Question not found" type="warning" />;

  const handleCheckboxChange = (e: CheckboxChangeEvent) => {
    const { value, checked } = e.target;
    setSelectedSources((prev) =>
      checked ? [...prev, value] : prev.filter((v) => v !== value)
    );
  };

  const handleSubmit = async () => {
    if (selectedSources.length > 0) {
      onChange({ sources: selectedSources });

      await Promise.all(
        selectedSources.map(async (sourceId) => {
          createSurveyResponse(surveyQuestion.id, sourceId);
        })
      );
      onComplete();
    }
  };

  return (
    <Container>
      <Question>
        <UpperCaseHeading>
          {t(surveyQuestion.questionText.trim())}
        </UpperCaseHeading>
      </Question>
      <AnswersCard bodyStyle={{ padding: 0 }}>
        {surveyQuestion.surveyAnswers.map((answer: any) => (
          <ResponsiveGrid key={answer.id}>
            <CardContent
              key={answer.id}
              isSelected={selectedSources.includes(answer.id)}
              onClick={() =>
                handleCheckboxChange({
                  target: {
                    value: answer.id,
                    checked: !selectedSources.includes(answer.id),
                  },
                } as CheckboxChangeEvent)
              }
            >
              <div className="icon-text-group">
                <IconContainer>{iconMapping[answer.id]}</IconContainer>
                <span>{t(answer.answerText.trim())}</span>
              </div>
              <StyledCheckbox
                checked={selectedSources.includes(answer.id)}
                onChange={handleCheckboxChange}
                value={answer.id}
              />
            </CardContent>
          </ResponsiveGrid>
        ))}
        <ContinueButton
          type="primary"
          onClick={handleSubmit}
          disabled={selectedSources.length === 0}
        >
          CONTINUE
        </ContinueButton>
      </AnswersCard>
    </Container>
  );
};

export default ReasonForLearning;
